import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Trash2, Edit, Plus } from 'lucide-react';
import type { MaterialConsumption } from '../../types/material';
import { materialConsumptionApi } from '../../api/materialApi';
import { useToast } from '../ui/use-toast';
import { MaterialConsumptionForm } from './MaterialConsumptionForm';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';

interface MaterialConsumptionListProps {
  workEntryId: number;
  addedForUserId: number;
  canEdit?: boolean;
  refreshTrigger?: number;
  onRefresh?: () => void;
}

export function MaterialConsumptionList({ 
  workEntryId, 
  addedForUserId, 
  canEdit = false,
  refreshTrigger,
  onRefresh
}: MaterialConsumptionListProps) {
  const [consumptions, setConsumptions] = useState<MaterialConsumption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const { toast } = useToast();

  const handleAddSuccess = (newConsumption: MaterialConsumption) => {
    // Optimistic update: add to UI immediately
    setConsumptions(prev => [...prev, newConsumption]);
    setShowAddForm(false);
    // Notify parent components to refresh
    onRefresh?.();
  };

  const loadConsumptions = async () => {
    try {
      setIsLoading(true);
      const data = await materialConsumptionApi.getByWorkEntry(workEntryId);
      setConsumptions(data);
    } catch (error) {
      console.error('Error loading consumptions:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca consumurile de materiale',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConsumptions();
  }, [workEntryId, refreshTrigger]);

  const handleDelete = async (id: number) => {
    // Optimistic update: remove from UI immediately
    const originalConsumptions = [...consumptions];
    setConsumptions(prev => prev.filter(c => c.id !== id));

    try {
      await materialConsumptionApi.delete(id);
      toast({
        title: 'Succes',
        description: 'Consumul a fost șters cu succes'
      });
    } catch (error) {
      // Rollback optimistic update on error
      setConsumptions(originalConsumptions);
      console.error('Error deleting consumption:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut șterge consumul',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatQuantity = (quantity: number, unitSymbol?: string) => {
    return `${quantity.toLocaleString('ro-RO')} ${unitSymbol || ''}`.trim();
  };

  if (isLoading) {
    return <div className="text-center py-4">Se încarcă consumurile...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Materiale Consumate</h3>
        {canEdit && (
          <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Material
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Adaugă Consum Material</DialogTitle>
                <DialogDescription>
                  Înregistrează consumul de material pentru această activitate de lucru.
                </DialogDescription>
              </DialogHeader>
              <MaterialConsumptionForm
                workEntryId={workEntryId}
                addedForUserId={addedForUserId}
                onSuccess={handleAddSuccess}
                onCancel={() => setShowAddForm(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {consumptions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <p className="text-muted-foreground text-center">
              Nu au fost înregistrate consumuri de materiale pentru această activitate.
            </p>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setShowAddForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adaugă primul material
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {consumptions.map((consumption) => (
            <Card key={consumption.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">
                      {consumption.materialTypeName}
                    </CardTitle>
                    <CardDescription>
                      Cantitate: <strong>{formatQuantity(consumption.quantity, consumption.unitSymbol)}</strong>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {consumption.unitName}
                    </Badge>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmare ștergere</AlertDialogTitle>
                              <AlertDialogDescription>
                                Ești sigur că vrei să ștergi acest consum de material? 
                                Această acțiune nu poate fi anulată.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Anulează</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(consumption.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Șterge
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              {(consumption.notes || consumption.addedByUserName !== consumption.addedForUserName) && (
                <CardContent className="pt-0">
                  {consumption.notes && (
                    <p className="text-sm text-muted-foreground mb-2">
                      <strong>Observații:</strong> {consumption.notes}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {consumption.addedByUserName !== consumption.addedForUserName ? (
                        <>Adăugat de <strong>{consumption.addedByUserName}</strong> pentru <strong>{consumption.addedForUserName}</strong></>
                      ) : (
                        <>Adăugat de <strong>{consumption.addedByUserName}</strong></>
                      )}
                    </span>
                    <span>{formatDate(consumption.createdAt)}</span>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}